/**
 * API Exports
 *
 * Central export point for all API modules.
 * Automatically switches between mock and real APIs based on configuration.
 */

import { apiConfig } from './config'

// Re-export config
export { apiConfig } from './config'

// Re-export HTTP client utilities
export { http, httpClient, getStoredTokens, storeTokens, clearStoredTokens } from './httpClient'

// Mock database (only used when VITE_USE_MOCK_API=true)
export { mockDb, generateId, timestamp, delay } from './mockDb'
export type { CollectionName } from './mockDb'

// API client utilities
export {
  success,
  error,
  paginated,
  withDelay,
  safeExecute,
  isValidEmail,
  isValidPassword,
  calculateAge,
} from './client'

// ---------------------------------------------------------------------------
// Auth API
// ---------------------------------------------------------------------------
import { authApi as mockAuthApi } from './auth.api'
import { realAuthApi } from './auth.real.api'
export const authApi = apiConfig.useMockApi ? mockAuthApi : realAuthApi
export type { GoogleAuthData, AppleAuthData, OtpVerifyData } from './auth.real.api'

// ---------------------------------------------------------------------------
// User API
// ---------------------------------------------------------------------------
import { userApi as mockUserApi } from './user.api'
import { realUserApi } from './user.real.api'
export const userApi = apiConfig.useMockApi ? mockUserApi : realUserApi

// ---------------------------------------------------------------------------
// Challenge API
// ---------------------------------------------------------------------------
import { challengeApi as mockChallengeApi } from './challenge.api'
import { realChallengeApi } from './challenge.real.api'
export const challengeApi = apiConfig.useMockApi ? mockChallengeApi : realChallengeApi

// ---------------------------------------------------------------------------
// Track Record API
// ---------------------------------------------------------------------------
import { trackRecordApi as mockTrackRecordApi } from './trackRecord.api'
import { realTrackRecordApi } from './trackRecord.real.api'
export const trackRecordApi = apiConfig.useMockApi ? mockTrackRecordApi : realTrackRecordApi

// ---------------------------------------------------------------------------
// Explore API
// ---------------------------------------------------------------------------
import { exploreApi as mockExploreApi } from './explore.api'
import { realExploreApi } from './explore.real.api'
export const exploreApi = apiConfig.useMockApi ? mockExploreApi : realExploreApi

// ---------------------------------------------------------------------------
// Community API
// ---------------------------------------------------------------------------
import { communityApi as mockCommunityApi } from './community.api'
import { realCommunityApi } from './community.real.api'
export const communityApi = apiConfig.useMockApi ? mockCommunityApi : realCommunityApi

// ---------------------------------------------------------------------------
// Gift API
// ---------------------------------------------------------------------------
import { giftApi as mockGiftApi } from './gift.api'
import { realGiftApi } from './gift.real.api'
export const giftApi = apiConfig.useMockApi ? mockGiftApi : realGiftApi

// ---------------------------------------------------------------------------
// School API
// ---------------------------------------------------------------------------
import { schoolApi as mockSchoolApi } from './school.api'
import { realSchoolApi } from './school.real.api'
export const schoolApi = apiConfig.useMockApi ? mockSchoolApi : realSchoolApi

// ---------------------------------------------------------------------------
// Notification API
// ---------------------------------------------------------------------------
import { notificationApi as mockNotificationApi } from './notification.api'
import { realNotificationApi } from './notification.real.api'
export const notificationApi = apiConfig.useMockApi ? mockNotificationApi : realNotificationApi

// ---------------------------------------------------------------------------
// Gamification API
// ---------------------------------------------------------------------------
import * as mockGamificationApi from './gamification.api'
import * as realGamificationApi from './gamification.real.api'
export const gamificationApi = apiConfig.useMockApi ? mockGamificationApi : realGamificationApi

// ---------------------------------------------------------------------------
// Learning Path API
// ---------------------------------------------------------------------------
import { learningPathApi as mockLearningPathApi } from './learningPath.api'
import { realLearningPathApi } from './learningPath.real.api'
export const learningPathApi = apiConfig.useMockApi ? mockLearningPathApi : realLearningPathApi

// ---------------------------------------------------------------------------
// Portfolio API
// ---------------------------------------------------------------------------
import { portfolioApi as mockPortfolioApi } from './portfolio.api'
import { realPortfolioApi } from './portfolio.real.api'
export const portfolioApi = apiConfig.useMockApi ? mockPortfolioApi : realPortfolioApi

// ---------------------------------------------------------------------------
// Event API
// ---------------------------------------------------------------------------
import { eventApi as mockEventApi } from './event.api'
import { realEventApi } from './event.real.api'
export const eventApi = apiConfig.useMockApi ? mockEventApi : realEventApi

// ---------------------------------------------------------------------------
// Community Goal API
// ---------------------------------------------------------------------------
import { communityGoalApi as mockCommunityGoalApi } from './communityGoal.api'
import { realCommunityGoalApi } from './communityGoal.real.api'
export const communityGoalApi = apiConfig.useMockApi ? mockCommunityGoalApi : realCommunityGoalApi

// ---------------------------------------------------------------------------
// Payment API
// ---------------------------------------------------------------------------
import { paymentApi as mockPaymentApi } from './payment.api'
import { realPaymentApi } from './payment.real.api'
export const paymentApi = apiConfig.useMockApi ? mockPaymentApi : realPaymentApi

// ---------------------------------------------------------------------------
// Email Verification API
// ---------------------------------------------------------------------------
import { emailVerificationApi as mockEmailVerificationApi } from './emailVerification.api'
import { realEmailVerificationApi } from './emailVerification.real.api'
export const emailVerificationApi = apiConfig.useMockApi ? mockEmailVerificationApi : realEmailVerificationApi

// ---------------------------------------------------------------------------
// Moderation API
// ---------------------------------------------------------------------------
export { moderationApi } from './moderation.api'

// ---------------------------------------------------------------------------
// Partner API
// ---------------------------------------------------------------------------
export { partnerApi } from './partner.api'

// ---------------------------------------------------------------------------
// Invitation API
// ---------------------------------------------------------------------------
import { invitationApi as mockInvitationApi } from './invitation.api'
import { realInvitationApi } from './invitation.real.api'
export const invitationApi = apiConfig.useMockApi ? mockInvitationApi : realInvitationApi

// ---------------------------------------------------------------------------
// Reflection API
// ---------------------------------------------------------------------------
import { reflectionApi as mockReflectionApi } from './reflection.api'
import { realReflectionApi } from './reflection.real.api'
export const reflectionApi = apiConfig.useMockApi ? mockReflectionApi : realReflectionApi

// ---------------------------------------------------------------------------
// Onboarding API
// ---------------------------------------------------------------------------
import { onboardingApi as mockOnboardingApi } from './onboarding.api'
import { realOnboardingApi } from './onboarding.real.api'
export const onboardingApi = apiConfig.useMockApi ? mockOnboardingApi : realOnboardingApi

// ---------------------------------------------------------------------------
// Sharing API
// ---------------------------------------------------------------------------
import { sharingApi as mockSharingApi } from './sharing.api'
import { realSharingApi } from './sharing.real.api'
export const sharingApi = apiConfig.useMockApi ? mockSharingApi : realSharingApi

// ---------------------------------------------------------------------------
// Admin API
// ---------------------------------------------------------------------------
import { adminApi as mockAdminApi } from './admin.api'
import { realAdminApi } from './admin.real.api'
export const adminApi = apiConfig.useMockApi ? mockAdminApi : realAdminApi

// ---------------------------------------------------------------------------
// Dealer's Choice API
// ---------------------------------------------------------------------------
import { dealersChoiceApi as mockDealersChoiceApi } from './dealersChoice.api'
import { realDealersChoiceApi } from './dealersChoice.real.api'
export const dealersChoiceApi = apiConfig.useMockApi ? mockDealersChoiceApi : realDealersChoiceApi

// ---------------------------------------------------------------------------
// Wallet API
// ---------------------------------------------------------------------------
import { walletApi as mockWalletApi } from './wallet.api'
import { realWalletApi } from './wallet.real.api'
export const walletApi = apiConfig.useMockApi ? mockWalletApi : realWalletApi

// ---------------------------------------------------------------------------
// Series API (real only — mock uses challenge API for series)
// ---------------------------------------------------------------------------
export { realSeriesApi } from './series.real.api'

// ---------------------------------------------------------------------------
// Vendor API (public profile)
// ---------------------------------------------------------------------------
import { vendorApi as mockVendorApi } from './vendor.api'
import { realVendorApi } from './vendor.real.api'
export const vendorApi = apiConfig.useMockApi ? mockVendorApi : (mockVendorApi as typeof mockVendorApi)

// ---------------------------------------------------------------------------
// Feedback API (provider feedback from users)
// ---------------------------------------------------------------------------
import { feedbackApi as mockFeedbackApi } from './feedback.api'
import { realFeedbackApi } from './feedback.real.api'
export const feedbackApi = apiConfig.useMockApi ? mockFeedbackApi : realFeedbackApi

// ---------------------------------------------------------------------------
// Tracker API (internal product tracker)
// ---------------------------------------------------------------------------
import { trackerApi as mockTrackerApi } from './tracker.api'
import { realTrackerApi } from './tracker.real.api'
export const trackerApi = apiConfig.useMockApi ? mockTrackerApi : realTrackerApi

// ---------------------------------------------------------------------------
// Seeding (only for mock API)
// ---------------------------------------------------------------------------
export { seedDatabase, resetDatabase } from './seed'
